import React, { useState, useEffect, useCallback } from 'react';
import ProjectOverlay from './ProjectOverlay';
import { siteData, navItems } from '../../data/portfolioData';

const PortfolioSite = () => {
  const [activeProject, setActiveProject] = useState(null); // navItem object or null
  const [activeSubItem, setActiveSubItem] = useState(null); // subItem object or null
  const [hoverPreview, setHoverPreview] = useState(null); // preview string or null

  // The project passed to the overlay: sub-item when music is active, else the top-level item
  const effectiveProject =
    activeProject?.type === 'music' ? activeSubItem : activeProject;

  // Close overlay on Escape
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape' && activeProject) {
        setActiveProject(null);
        setActiveSubItem(null);
      }
    },
    [activeProject]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Sync hash with active project — stays on / so Gatsby never 404s
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const slug = activeSubItem?.slug || activeProject?.slug;
    window.history.replaceState({}, '', slug ? `/#${slug}` : '/');
  }, [activeProject, activeSubItem]);

  // Restore active project from hash on first load
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      // Check top-level items first
      const found = navItems.find((i) => i.slug === hash);
      if (found) {
        setActiveProject(found);
        if (found.type === 'music' && found.subItems?.length) {
          setActiveSubItem(found.subItems[0]);
        }
        return;
      }
      // Check sub-items
      for (const item of navItems) {
        if (item.subItems) {
          const sub = item.subItems.find((s) => s.slug === hash);
          if (sub) {
            setActiveProject(item);
            setActiveSubItem(sub);
            return;
          }
        }
      }
    }
  }, []);

  const handleNavClick = (item) => {
    if (activeProject?.slug === item.slug) {
      // Toggle off
      setActiveProject(null);
      setActiveSubItem(null);
    } else {
      setActiveProject(item);
      if (item.type === 'music' && item.subItems?.length) {
        setActiveSubItem(item.subItems[0]);
      } else {
        setActiveSubItem(null);
      }
    }
  };

  const handleSubNavClick = (sub) => {
    setActiveSubItem(activeSubItem?.slug === sub.slug ? activeSubItem : sub);
  };

  const handleTitleClick = () => {
    setActiveProject(null);
    setActiveSubItem(null);
  };

  const isMusicActive = activeProject?.type === 'music';

  return (
    <>
      {/* ── Top-left: site name ─────────────────────────────── */}
      <button
        className="pf-title"
        onClick={handleTitleClick}
        aria-label="Home"
        type="button"
      >
        {siteData.name.toLowerCase()}
      </button>

      {/* ── Bottom nav bar ──────────────────────────────────── */}
      <nav className="pf-bottom" aria-label="Selected work">
        {/* Sub-nav: visible when music is active */}
        <div className={`pf-subnav${isMusicActive ? ' pf-subnav--visible' : ''}`}>
          {isMusicActive && (activeProject.subItems || []).map((sub, i, arr) => (
            <button
              key={sub.slug}
              className={`pf-subnav__link${activeSubItem?.slug === sub.slug ? ' pf-subnav__link--active' : ''}`}
              onClick={() => handleSubNavClick(sub)}
              type="button"
            >
              {sub.label}{i < arr.length - 1 ? ',' : ''}
            </button>
          ))}
        </div>

        <div className="pf-bottom__nav">
          {navItems.map((item) => (
            <button
              key={item.id}
              className="pf-bottom__link"
              onClick={() => handleNavClick(item)}
              onMouseEnter={() => setHoverPreview(item.preview)}
              onMouseLeave={() => setHoverPreview(null)}
              type="button"
              aria-current={activeProject?.slug === item.slug ? 'page' : undefined}
            >
              {item.label},<sup>{item.id}</sup>
              {item.id < navItems.length ? ' ' : ''}
            </button>
          ))}

          {/* Preview text – fades in on nav hover */}
          <span
            className={`pf-bottom__preview${hoverPreview ? ' pf-bottom__preview--visible' : ''}`}
            aria-hidden="true"
          >
            {hoverPreview}
          </span>
        </div>
      </nav>

      {/* ── Project overlay (sits above bottom bar, below title) */}
      <ProjectOverlay
        project={effectiveProject}
        visible={!!effectiveProject}
        onClose={() => { setActiveProject(null); setActiveSubItem(null); }}
      />

    </>
  );
};

export default PortfolioSite;
